import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Flame, Star, ChevronRight, Play, Clock, Target, Trophy, ArrowRight, BookOpen, TrendingUp, Sparkles } from 'lucide-react';
import { examsApi, publicConfigApi, type HomepageSectionData } from '@/services/api';
import { usePaymentGate } from '@/payment';
import { useGamificationStore } from '@/store/useGamificationStore';
import { usePracticeStore } from '@/store/usePracticeStore';
import { getContentLocale, pickLocalizedText } from '@/lib/contentLocale';
import { ExamAffiliationNotice } from '@/components/ExamAffiliationNotice';
import { trackSearch, trackSuperCategorySelect } from '@/lib/track';

interface ApiExam {
  id: string;
  name: string;
  description: string;
  icon: string;
  bgGradient: string;
  totalQuestions: number;
  duration: number;
  passingScore: number;
  hasOralQuestions: boolean;
  hasCodeQuestions: boolean;
  hasImageQuestions: boolean;
  homepageSectionSlug: string | null;
  categories: { id: string; name: string }[];
}

interface RecentExamEntry {
  examId: string;
  lastScore: number;
  lastDate: string;
  sessionCount: number;
  bestScore: number;
}

function getRecentExams(completedSessions: { examId: string; score: number; completedAt: string }[]): RecentExamEntry[] {
  const map = new Map<string, RecentExamEntry>();
  for (const s of completedSessions) {
    const existing = map.get(s.examId);
    if (!existing) {
      map.set(s.examId, {
        examId: s.examId,
        lastScore: s.score,
        lastDate: s.completedAt,
        sessionCount: 1,
        bestScore: s.score,
      });
    } else {
      existing.sessionCount++;
      if (s.completedAt > existing.lastDate) {
        existing.lastScore = s.score;
        existing.lastDate = s.completedAt;
      }
      if (s.score > existing.bestScore) existing.bestScore = s.score;
    }
  }
  return Array.from(map.values()).sort((a, b) => b.lastDate.localeCompare(a.lastDate));
}

function timeAgo(dateStr: string, locale: 'fr' | 'en'): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return locale === 'en' ? 'Just now' : "À l'instant";
  if (mins < 60) return locale === 'en' ? `${mins}m ago` : `Il y a ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return locale === 'en' ? `${hours}h ago` : `Il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return locale === 'en' ? `${days}d ago` : `Il y a ${days}j`;
  return locale === 'en' ? `${Math.floor(days / 7)}w ago` : `Il y a ${Math.floor(days / 7)}sem`;
}

export function DashboardPage() {
  const navigate = useNavigate();
  const [exams, setExams] = useState<ApiExam[]>([]);
  const [sections, setSections] = useState<HomepageSectionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { streak, totalXP, level, getXPProgress, quizCount } = useGamificationStore();
  const { completedSessions } = usePracticeStore();
  const locale = getContentLocale();

  useEffect(() => {
    Promise.all([
      examsApi.getAll(),
      publicConfigApi.getHomepageSections().catch(() => ({ data: [] as HomepageSectionData[] })),
    ]).then(([examsRes, sectionsRes]) => {
      const items: any[] = Array.isArray(examsRes.data) ? examsRes.data : examsRes.data.items ?? [];
      setExams(items.map((e: any) => ({
        id: e.id,
        name: e.name,
        description: e.description || '',
        icon: e.icon || '📚',
        bgGradient: e.bgGradient || 'from-emerald-500 to-teal-600',
        totalQuestions: e.totalQuestions || 0,
        duration: e.duration || 60,
        passingScore: e.passingScore || 70,
        hasOralQuestions: !!e.hasOralQuestions,
        hasCodeQuestions: !!e.hasCodeQuestions,
        hasImageQuestions: !!e.hasImageQuestions,
        homepageSectionSlug: e.homepageSectionSlug ?? null,
        categories: e.categories || [],
      })));
      setSections(sectionsRes.data.filter((s) => s.isVisible));
    }).catch(() => {
      setExams([]);
      setSections([]);
    }).finally(() => setLoading(false));
  }, []);

  const recentExams = useMemo(() => getRecentExams(completedSessions), [completedSessions]);

  const examMap = useMemo(() => {
    const m = new Map<string, ApiExam>();
    exams.forEach((e) => m.set(e.id, e));
    return m;
  }, [exams]);

  const recentWithData = useMemo(
    () => recentExams.filter((r) => examMap.has(r.examId)).slice(0, 8),
    [recentExams, examMap],
  );

  const lastExam = recentWithData[0];
  const lastExamData = lastExam ? examMap.get(lastExam.examId) : undefined;

  const filteredExams = exams.filter(exam =>
    exam.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (exam.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isSearching = searchQuery.length > 0;

  // Suivi GA4 des recherches : débounce 700ms pour ne loguer que la requête
  // « stabilisée » (pas chaque frappe), avec le nombre de résultats obtenus.
  useEffect(() => {
    const term = searchQuery.trim();
    if (!term) return;
    const timer = setTimeout(() => trackSearch(term, filteredExams.length), 700);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const getExamsForSection = (section: { slug: string; featuredExamIds: string[] | null }) => {
    const ids = section.featuredExamIds;
    if (ids && ids.length > 0) {
      return ids.map((id) => exams.find((e) => e.id === id)).filter(Boolean) as ApiExam[];
    }
    return filteredExams.filter((e) => e.homepageSectionSlug === section.slug);
  };

  const featuredExamIdSet = useMemo(() => {
    const set = new Set<string>();
    sections.forEach((s) => s.featuredExamIds?.forEach((id) => set.add(id)));
    return set;
  }, [sections]);

  const unsectionedExams = filteredExams.filter(
    (e) => !featuredExamIdSet.has(e.id) && (!e.homepageSectionSlug || !sections.some((s) => s.slug === e.homepageSectionSlug))
  );

  const xp = getXPProgress();

  const t = locale === 'en' ? {
    hello: 'Hello!',
    subtitle: 'Ready to learn today?',
    search: 'Search an exam...',
    continue: 'Continue',
    continueStudying: 'Continue studying',
    lastScore: 'Last score',
    bestScore: 'Best',
    sessions: 'sessions',
    recentlyStudied: 'Recently studied',
    seeAll: 'See all',
    noExams: 'No exams found',
    otherExams: 'Other exams',
    noCategoryExams: 'No exams in this category yet',
    level: 'Level',
    quizzes: 'Quizzes',
    streak: 'Streak',
    yourProgress: 'Your progress',
    startFirstQuiz: 'Start your first quiz to track your progress!',
    exploreExams: 'Explore exams',
  } : {
    hello: 'Bonjour !',
    subtitle: "Prêt à apprendre aujourd'hui ?",
    search: 'Rechercher un examen...',
    continue: 'Continuer',
    continueStudying: "Reprendre l'étude",
    lastScore: 'Dernier score',
    bestScore: 'Meilleur',
    sessions: 'sessions',
    recentlyStudied: 'Étudiés récemment',
    seeAll: 'Voir tout',
    noExams: 'Aucun examen trouvé',
    otherExams: 'Autres examens',
    noCategoryExams: 'Aucun examen dans cette catégorie',
    level: 'Niveau',
    quizzes: 'Quiz',
    streak: 'Streak',
    yourProgress: 'Votre progression',
    startFirstQuiz: 'Lancez votre premier quiz pour suivre votre progression !',
    exploreExams: 'Explorer les examens',
  };

  return (
    <div className="flex flex-col h-full bg-app pb-20 lg:pb-0">
      {/* Header */}
      <div className="flex-shrink-0 px-4 sm:px-5 lg:px-6 pt-5 sm:pt-6 pb-3 sm:pb-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4 sm:mb-5">
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-fg">{t.hello}</h1>
              <p className="text-xs sm:text-sm text-fg-subtle mt-0.5">{t.subtitle}</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 mr-14 lg:mr-0">
              {streak > 0 && (
                <div className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 bg-orange-500/10 border border-orange-500/20 rounded-full">
                  <Flame className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-400" />
                  <span className="text-[10px] sm:text-xs font-semibold text-orange-400">{streak}j</span>
                </div>
              )}
              {totalXP > 0 && (
                <div className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 bg-yellow-500/10 border border-yellow-500/20 rounded-full">
                  <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-400" />
                  <span className="text-[10px] sm:text-xs font-semibold text-yellow-400">{totalXP.toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-faint" />
            <input
              type="text"
              placeholder={t.search}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-overlay-1 border border-line text-fg rounded-xl focus:outline-none focus:border-green-500/40 focus:bg-overlay-2 placeholder-fg-faint text-sm transition-all"
            />
          </div>
          <div className="mt-2.5">
            <ExamAffiliationNotice variant="banner" />
          </div>
          <QuotaHint locale={locale} />
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-5 lg:px-6 pb-6">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : isSearching ? (
            /* Search results — flat grid, no sections */
            <>
              {filteredExams.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {filteredExams.map((exam) => (
                    <ExamCard key={exam.id} exam={exam} onClick={() => navigate(`/exam/${exam.id}`)} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-fg-faint">
                  <Search className="w-12 h-12 mb-3 opacity-50" />
                  <p className="text-sm font-medium">{t.noExams}</p>
                </div>
              )}
            </>
          ) : (
            /* Default view with recent exams + sections */
            <div className={quizCount > 0 || recentWithData.length > 0 ? 'lg:grid lg:grid-cols-[1fr_300px] xl:grid-cols-[1fr_340px] lg:gap-6 lg:items-start' : ''}>
              {/* Main column */}
              <div className="min-w-0">
                {/* Continue studying hero */}
                {lastExam && lastExamData && (
                  <button
                    onClick={() => navigate(`/exam/${lastExam.examId}`)}
                    className="w-full text-left mb-5 group"
                  >
                    <div className={`relative overflow-hidden bg-gradient-to-r ${lastExamData.bgGradient} rounded-2xl p-4 sm:p-5 shadow-lg`}>
                      <div className="absolute inset-0 bg-black/30" />
                      <div className="relative flex items-center gap-4">
                        <div className="w-14 h-14 sm:w-16 sm:h-16 bg-overlay-3 backdrop-blur-sm rounded-2xl flex items-center justify-center text-2xl sm:text-3xl flex-shrink-0">
                          {lastExamData.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] sm:text-xs font-medium text-white/70 uppercase tracking-wider mb-1">
                            {t.continueStudying}
                          </p>
                          <h2 className="text-sm sm:text-base lg:text-lg font-bold text-white truncate">
                            {lastExamData.name}
                          </h2>
                          <div className="flex items-center gap-3 mt-1.5 text-xs text-white/80">
                            <span className="flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              {t.lastScore}: {lastExam.lastScore}%
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {timeAgo(lastExam.lastDate, locale)}
                            </span>
                          </div>
                        </div>
                        <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-overlay-3 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:bg-overlay-3 transition-colors">
                          <Play className="w-5 h-5 sm:w-6 sm:h-6 text-white fill-white" />
                        </div>
                      </div>
                    </div>
                  </button>
                )}

                {/* Recently studied — horizontal scroll */}
                {recentWithData.length > 1 && (
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-fg-subtle" />
                        <h2 className="text-sm font-semibold text-fg-muted">{t.recentlyStudied}</h2>
                      </div>
                    </div>
                    <div className="flex gap-2.5 overflow-x-auto scrollbar-hide pb-1">
                      {recentWithData.slice(1).map((entry) => {
                        const examData = examMap.get(entry.examId)!;
                        const gradient = examData.bgGradient || 'from-emerald-500 to-teal-600';
                        return (
                          <button
                            key={entry.examId}
                            onClick={() => navigate(`/exam/${entry.examId}`)}
                            className="flex-shrink-0 w-[200px] sm:w-[220px] bg-surface border border-line rounded-xl p-3 hover:border-green-500/20 hover:bg-surface-2 transition-all text-left group"
                          >
                            <div className="flex items-center gap-2.5 mb-2">
                              <div className={`w-9 h-9 bg-gradient-to-br ${gradient} rounded-lg flex items-center justify-center text-base flex-shrink-0`}>
                                {examData.icon}
                              </div>
                              <h3 className="text-xs font-semibold text-fg truncate">{examData.name}</h3>
                            </div>
                            <div className="flex items-center justify-between text-[10px] text-fg-subtle">
                              <span>{entry.lastScore}% · {entry.sessionCount} {t.sessions}</span>
                              <ArrowRight className="w-3 h-3 text-fg-faint group-hover:text-green-400 transition-colors" />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Homepage Sections */}
                {sections.map((section) => {
                  const sectionExams = getExamsForSection(section);
                  return (
                    <SectionBlock
                      key={section.slug}
                      section={section}
                      exams={sectionExams}
                      locale={locale}
                      onExamClick={(id) => {
                        // Super-catégorie la plus utilisée (ouverture d'un examen).
                        trackSuperCategorySelect(section.slug, section.title, 'exam_click');
                        navigate(`/exam/${id}`);
                      }}
                      onSeeAll={() => {
                        trackSuperCategorySelect(section.slug, section.title, 'see_all');
                        navigate(`/certifications?section=${section.slug}`);
                      }}
                      t={t}
                    />
                  );
                })}

                {/* Unsectioned exams */}
                {unsectionedExams.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-sm font-semibold text-fg-muted uppercase tracking-wider">{t.otherExams}</h2>
                      <span className="text-xs text-fg-faint">{unsectionedExams.length}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                      {unsectionedExams.map((exam) => (
                        <ExamCard key={exam.id} exam={exam} onClick={() => navigate(`/exam/${exam.id}`)} />
                      ))}
                    </div>
                  </div>
                )}

                {filteredExams.length === 0 && sections.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-20 text-fg-faint">
                    <Search className="w-12 h-12 mb-3 opacity-50" />
                    <p className="text-sm font-medium">{t.noExams}</p>
                  </div>
                )}
              </div>

              {/* Desktop sidebar — progress + recent activity */}
              {(quizCount > 0 || recentWithData.length > 0) && (
                <div className="hidden lg:block space-y-4 sticky top-0">
                {quizCount > 0 && (
                  <div className="bg-surface border border-line rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingUp className="w-4 h-4 text-emerald-400" />
                      <h3 className="text-sm font-semibold text-fg">{t.yourProgress}</h3>
                    </div>

                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div className="bg-overlay-1 rounded-xl p-2.5 text-center">
                        <p className="text-base font-bold text-emerald-400">{level}</p>
                        <p className="text-[10px] text-fg-faint mt-0.5">{t.level}</p>
                      </div>
                      <div className="bg-overlay-1 rounded-xl p-2.5 text-center">
                        <p className="text-base font-bold text-blue-400">{quizCount}</p>
                        <p className="text-[10px] text-fg-faint mt-0.5">{t.quizzes}</p>
                      </div>
                      <div className="bg-overlay-1 rounded-xl p-2.5 text-center">
                        <p className="text-base font-bold text-orange-400">{streak}j</p>
                        <p className="text-[10px] text-fg-faint mt-0.5">{t.streak}</p>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] text-fg-subtle">{t.level} {level}</span>
                        <span className="text-[10px] text-fg-subtle">{t.level} {level + 1}</span>
                      </div>
                      <div className="h-2 bg-overlay-2 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-green-400 rounded-full transition-all duration-500"
                          style={{ width: `${xp.percent}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-fg-faint text-center mt-1.5">
                        {xp.current} / {xp.needed} XP
                      </p>
                    </div>
                  </div>
                )}

                {/* Recent sessions list */}
                {recentWithData.length > 0 && (
                  <div className="bg-surface border border-line rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-4 h-4 text-blue-400" />
                      <h3 className="text-sm font-semibold text-fg">{t.recentlyStudied}</h3>
                    </div>
                    <div className="space-y-1">
                      {recentWithData.slice(0, 6).map((entry) => {
                        const examData = examMap.get(entry.examId)!;
                        const gradient = examData.bgGradient || 'from-emerald-500 to-teal-600';
                        const scoreColor = entry.lastScore >= 80 ? 'text-green-400' : entry.lastScore >= 50 ? 'text-yellow-400' : 'text-red-400';
                        return (
                          <button
                            key={entry.examId}
                            onClick={() => navigate(`/exam/${entry.examId}`)}
                            className="w-full flex items-center gap-2.5 p-2 rounded-lg hover:bg-overlay-1 transition-colors text-left group"
                          >
                            <div className={`w-8 h-8 bg-gradient-to-br ${gradient} rounded-lg flex items-center justify-center text-sm flex-shrink-0`}>
                              {examData.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-fg truncate">{examData.name}</p>
                              <p className="text-[10px] text-fg-faint">{timeAgo(entry.lastDate, locale)}</p>
                            </div>
                            <div className="flex-shrink-0 text-right">
                              <p className={`text-xs font-bold ${scoreColor}`}>{entry.lastScore}%</p>
                              <p className="text-[10px] text-fg-faint">{t.bestScore}: {entry.bestScore}%</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Compteur d'essais gratuits restants du jour. N'apparaît que si le module de
 * paiement est actif ET que l'utilisateur a des limites (non-abonné). Affiché au
 * moment où la valeur perçue est haute (accueil) : c'est le meilleur emplacement
 * d'upsell — l'utilisateur voit ce qu'il lui reste avant de heurter le paywall.
 */
function QuotaHint({ locale }: { locale: 'fr' | 'en' }) {
  const { paymentsEnabled, quota, hasActiveSubscription } = usePaymentGate();
  if (!paymentsEnabled || hasActiveSubscription || !quota) return null;

  const practice = quota.practiceRemaining;
  const simulation = quota.simulationRemaining;
  const parts: string[] = [];
  if (practice !== null) {
    parts.push(
      locale === 'en'
        ? `${practice} practice ${practice === 1 ? 'session' : 'sessions'}`
        : `${practice} ${practice === 1 ? 'pratique' : 'pratiques'}`,
    );
  }
  if (simulation !== null) {
    parts.push(
      locale === 'en'
        ? `${simulation} ${simulation === 1 ? 'simulation' : 'simulations'}`
        : `${simulation} simulation${simulation === 1 ? '' : 's'}`,
    );
  }
  if (parts.length === 0) return null;

  const exhausted = (practice ?? 1) <= 0 && (simulation ?? 1) <= 0;
  const list = parts.join(locale === 'en' ? ' and ' : ' et ');
  const text = exhausted
    ? locale === 'en'
      ? 'You have used all your free tries for today.'
      : "Vous avez épuisé vos essais gratuits du jour."
    : locale === 'en'
      ? `Free today: ${list} left.`
      : `Gratuit aujourd'hui : il vous reste ${list}.`;
  const cta = locale === 'en' ? 'Go unlimited' : 'Passer en illimité';

  return (
    <Link
      to="/plans"
      className={`mt-3 flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-medium transition-colors ${
        exhausted
          ? 'border-amber-500/30 bg-amber-500/10 text-amber-300 hover:bg-amber-500/15'
          : 'border-line bg-overlay-1 text-fg-subtle hover:bg-overlay-2'
      }`}
    >
      <Sparkles className="w-3.5 h-3.5 flex-shrink-0 text-amber-400" />
      <span className="flex-1">{text}</span>
      <span className="flex items-center gap-0.5 text-green-400">
        {cta}
        <ArrowRight className="w-3 h-3" />
      </span>
    </Link>
  );
}

interface SectionBlockProps {
  section: HomepageSectionData;
  exams: ApiExam[];
  locale: 'fr' | 'en';
  onExamClick: (id: string) => void;
  onSeeAll: () => void;
  t: Record<string, string>;
}

function SectionBlock({ section, exams, locale, onExamClick, onSeeAll, t }: SectionBlockProps) {
  const title = pickLocalizedText(locale, section.title, section.titleEn);
  const description = pickLocalizedText(locale, section.description, section.descriptionEn);

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {section.icon && <span className="text-lg">{section.icon}</span>}
          <h2 className="text-sm font-semibold text-fg uppercase tracking-wider">{title}</h2>
        </div>
        {exams.length > 3 && (
          <button onClick={onSeeAll} className="flex items-center gap-1 text-xs text-green-400 hover:text-green-300 transition-colors">
            {t.seeAll}
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {description && (
        <p className="text-xs text-fg-faint mb-3 -mt-1">{description}</p>
      )}
      {exams.length === 0 ? (
        <div className="bg-overlay-1 border border-line rounded-xl p-4 text-center">
          <p className="text-xs text-fg-faint">{t.noCategoryExams}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3">
          {exams.slice(0, 6).map((exam) => (
            <ExamCard key={exam.id} exam={exam} onClick={() => onExamClick(exam.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function ExamCard({ exam, onClick }: { exam: ApiExam; onClick: () => void }) {
  const gradient = exam.bgGradient || 'from-emerald-500 to-teal-600';
  return (
    <button
      onClick={onClick}
      className="group text-left bg-surface border border-line rounded-2xl p-4 hover:border-green-500/20 hover:bg-surface-2 transition-all duration-300"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-11 h-11 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center text-xl flex-shrink-0`}>
          {exam.icon || '📚'}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-fg truncate">{exam.name}</h3>
          <p className="text-xs text-fg-faint mt-0.5 line-clamp-1">{exam.description}</p>
        </div>
      </div>

      <div className="flex items-center gap-3 text-xs text-fg-subtle mb-3">
        <span>{exam.totalQuestions} Q</span>
        <span className="w-1 h-1 bg-overlay-3 rounded-full" />
        <span>{exam.duration} min</span>
        <span className="w-1 h-1 bg-overlay-3 rounded-full" />
        <span>{exam.passingScore}%</span>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {exam.hasOralQuestions && (
          <span className="text-[10px] px-2 py-0.5 bg-purple-500/10 text-purple-300/80 rounded-md border border-purple-500/10">Audio</span>
        )}
        {exam.hasCodeQuestions && (
          <span className="text-[10px] px-2 py-0.5 bg-blue-500/10 text-blue-300/80 rounded-md border border-blue-500/10">Code</span>
        )}
        {exam.hasImageQuestions && (
          <span className="text-[10px] px-2 py-0.5 bg-emerald-500/10 text-emerald-300/80 rounded-md border border-emerald-500/10">Images</span>
        )}
        <span className="text-[10px] px-2 py-0.5 bg-overlay-1 text-fg-faint rounded-md border border-line">
          {exam.categories.length} catégories
        </span>
      </div>
    </button>
  );
}
